// Applies the company's brand (Settings → Brand) to the whole app: colours, window title and tab icon.
const HEX = /^#[0-9a-f]{6}$/i;

function mixWithWhite(hex, t) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c => Math.round(c + (255 - c) * t));
  return `#${ch.map(c => c.toString(16).padStart(2, "0")).join("")}`;
}

const toRgb = (hex) => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const toHex = (rgb) => `#${rgb.map(c => Math.round(c).toString(16).padStart(2, "0")).join("")}`;

// WCAG relative luminance and contrast ratio (4.5 is the minimum for body text, 3 for large text and buttons).
export function luminance(hex) {
  const [r, g, b] = toRgb(hex).map(c => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
// Text colour that stays readable on a brand colour: white or near-black, whichever has more contrast.
export function onColor(bg) { return contrast(bg, "#ffffff") >= contrast(bg, "#111111") ? "#ffffff" : "#111111"; }

// The main colours of a logo: pixels sampled from the image, transparent / near-white / near-black / grey ones
// ignored, the rest grouped by hue, brightness and saturation. Returns up to `n` distinct "#rrggbb" colours, most
// common first. Pass the pixel data (RGBA) so this stays testable without a browser.
export function dominantColours(rgba, n = 3) {
  const bins = new Map();
  for (let i = 0; i < rgba.length; i += 4) {
    const [r, g, b, a] = [rgba[i], rgba[i + 1], rgba[i + 2], rgba[i + 3]];
    if (a < 200) continue;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mx > 235 && mn > 225) continue;          // white background
    if (mx < 35) continue;                        // black
    if (mx - mn < 28) continue;                   // grey: not a brand colour
    const key = `${r >> 5}-${g >> 5}-${b >> 5}`;
    const bin = bins.get(key) || { n: 0, r: 0, g: 0, b: 0 };
    bin.n++; bin.r += r; bin.g += g; bin.b += b;
    bins.set(key, bin);
  }
  const ranked = [...bins.values()].sort((x, y) => y.n - x.n).map(x => toHex([x.r / x.n, x.g / x.n, x.b / x.n]));
  const out = [];
  for (const hex of ranked) {
    const [r1, g1, b1] = toRgb(hex);
    if (out.every(o => { const [r2, g2, b2] = toRgb(o); return Math.hypot(r1 - r2, g1 - g2, b1 - b2) > 80; })) out.push(hex);
    if (out.length === n) break;
  }
  return out;
}

// In the browser: load an image (a data URL or file URL) and return its main colours.
export function suggestPalette(src, n = 3) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = 96, c = document.createElement("canvas");
        c.width = c.height = size;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        const scale = Math.min(size / img.width, size / img.height);
        ctx.drawImage(img, 0, 0, img.width * scale, img.height * scale);
        resolve(dominantColours(ctx.getImageData(0, 0, size, size).data, n));
      } catch { resolve([]); }
    };
    img.onerror = () => resolve([]);
    img.src = src;
  });
}

export function applyBrand(brand, logoUrl) {
  if (!brand) return;
  const root = document.documentElement.style;
  if (HEX.test(brand.primary_color || "") && brand.primary_color.toLowerCase() !== "#1e3a8a") {
    root.setProperty("--indigo", brand.primary_color);
    root.setProperty("--brand-soft", brand.primary_color);
    root.setProperty("--on-primary", onColor(brand.primary_color));
  } else {
    root.removeProperty("--indigo");
    root.removeProperty("--brand-soft");
    root.removeProperty("--on-primary");
  }
  if (HEX.test(brand.accent_color || "")) { root.setProperty("--orange", brand.accent_color); root.setProperty("--on-accent", onColor(brand.accent_color)); }
  else { root.removeProperty("--orange"); root.removeProperty("--on-accent"); }
  document.title = brand.company_name ? `${brand.company_name} · ${brand.product_name || "CRM"}` : (brand.product_name || "CRM");
  let icon = document.querySelector("link[rel='icon']");
  if (brand.has_logo) {
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = `${logoUrl}?v=${brand.logo_v || 0}`;
  } else if (icon) icon.remove();
}
